<script setup>
import SearchField from '@/components/fields/SearchField.vue'
import OurLossesCard from '@/components/about-raai/OurLossesCard.vue'
import { useOurLossesStore } from '@/store/ourLosses'
import { onMounted, ref, watch } from 'vue'
import * as helper from '@/store/helpers'
import debounce from 'lodash.debounce'

const ourLossesStore = useOurLossesStore()

const ourLossesCards = ref([])
const ourLossesCount = ref(0)
const searchStr = ref('')

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(15) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const getMaxPage = (ourLossesCount) => {
    return Math.ceil(ourLossesCount / paginationCount.value)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document.getElementById('about-page').scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

const getLosses = async (isEmpty = true, searchString = '') => {
    if (isEmpty) {
        ourLossesCount.value = await helper.api('GET', '/our_losses/count', {})
        await ourLossesStore.getLosses(
            0,
            paginationCount.value * pageNumber.value
        )
        ourLossesCards.value = ourLossesStore.allLosses
    } else {
        ourLossesCount.value = await helper.api('GET', '/our_losses/count', {
            search_string: searchString
        })
        await ourLossesStore.getLosses(
            paginationCount.value * (pageNumber.value - 1),
            paginationCount.value * pageNumber.value,
            searchString
        )
        ourLossesCards.value = ourLossesStore.allLosses
    }
}

watch(
    searchStr,
    debounce(async () => {
        changePage(1)
        await getLosses(false, searchStr.value)
        maxPage.value = getMaxPage(ourLossesCount.value)
    }, 500)
)

watch(pageNumber, async () => {
    await getLosses(false, searchStr.value)
})

onMounted(async () => {
    await getLosses()
    maxPage.value = getMaxPage(ourLossesCount.value)
})
</script>

<template>
    <h1 class="title">НАШИ ПОТЕРИ</h1>
    <search-field v-model="searchStr" />
    <v-row class="ml-n2 mr-n2">
        <v-col
            v-for="ourLoss in ourLossesCards"
            :key="ourLoss.id"
            class="pl-2 pr-2 pb-2 pt-2"
            cols="12"
            sm="6"
            md="4"
        >
            <OurLossesCard v-bind="ourLoss" />
        </v-col>
    </v-row>
    <v-container
        :fluid="true"
        class="pa-0 pagination"
        :class="{ 'd-none': maxPage < 2 }"
    >
        <div
            class="page-arrows"
            @click="changePage(1)"
        >
            <svg
                width="20"
                height="15"
                viewBox="0 0 20 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                    fill="#0076DE"
                />
                <path
                    d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                    fill="#0076DE"
                />
            </svg>
        </div>
        <div
            v-for="(page, i) in maxPage"
            v-show="
                (pageNumber < 5 ? page <= 5 : page < pageNumber + 3) &&
                (pageNumber < 5 ? true : page > pageNumber - 3)
            "
            :key="page"
            :ref="
                (el) => {
                    if (el) pageElems[i] = el
                }
            "
            class="page"
            :class="{ toggle: i === 0 }"
            @click="changePage(page)"
        >
            {{ page }}
        </div>
        <div
            class="page-arrows"
            @click="changePage(maxPage)"
        >
            <svg
                width="20"
                height="15"
                viewBox="0 0 20 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                    fill="#0076DE"
                />
                <path
                    d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                    fill="#0076DE"
                />
            </svg>
        </div>
    </v-container>
</template>

<style lang="scss" scoped>
.title {
    font-size: 36px;
    font-weight: 600;
    line-height: 40px;
    color: #0635ac;
    text-align: left;
    margin-bottom: 17px;

    @media screen and (max-width: 767px) {
        font-size: 28px;
        line-height: 36px;
        text-align: left;
    }
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}
</style>
